import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import * as React from 'react';
import { BlogPostRequest } from '../../../models/Blogs';

export interface IBlogPostContext {
	blogPost?: Api.IBlogPost;
	request?: BlogPostRequest;
	sendFromUser?: Partial<Api.IUser>;
	// RegEd submission (format + answers) captured on the editor screen; consumed by ScheduleBlogPost
	// after the create so the submission can be attached to the server-created approval request.
	regEdMetadata?: IRegEdSubmissionMetadata;
	// Prior rejected AR id when this draft is a revision; travels with the metadata to ScheduleBlogPost.
	regEdRevisesApprovalRequestId?: string | null;
	setBlogPost(blogPost: Api.IBlogPost): void;
	setRequest: (request: BlogPostRequest) => void;
	setSendFromUser: (user: Partial<Api.IUser>) => void;
	setRegEdMetadata: (metadata?: IRegEdSubmissionMetadata) => void;
	setRegEdRevisesApprovalRequestId: (approvalRequestId?: string | null) => void;
}

export const BlogPostContext = React.createContext<IBlogPostContext>(null);

export const BlogPostContextProvider = ({ children }: { children: React.ReactNode }) => {
	const context = useCreatePostContext();

	return <BlogPostContext.Provider value={context}>{children}</BlogPostContext.Provider>;
};

export const useCreatePostContext = () => {
	const [context, setContext] = React.useState<IBlogPostContext>({
		blogPost: null,
		setBlogPost: (blogPost: Api.IBlogPost) => {
			setContext(prev => ({ ...prev, blogPost }));
		},
		request: null,
		setRequest: (request: BlogPostRequest) => {
			setContext(prev => ({ ...prev, request }));
		},
		sendFromUser: null,
		setSendFromUser: (user: Partial<Api.IUser>) => {
			setContext(prev => ({ ...prev, sendFromUser: user }));
		},
		regEdMetadata: undefined,
		setRegEdMetadata: (metadata?: IRegEdSubmissionMetadata) => {
			setContext(prev => ({ ...prev, regEdMetadata: metadata }));
		},
		regEdRevisesApprovalRequestId: undefined,
		setRegEdRevisesApprovalRequestId: (approvalRequestId?: string | null) => {
			setContext(prev => ({ ...prev, regEdRevisesApprovalRequestId: approvalRequestId }));
		},
	});

	return context;
};
